/**
 * GeofenceFormWizard.jsx
 * Formulario de Cerca Virtual em Etapas Premium
 *
 * 3 Passos:
 * 1. Basico - Nome, Descricao (obrigatorio)
 * 2. Configuracoes - Calendario, Ocultar do mapa
 * 3. Atributos - Atributos customizados (opcional)
 *
 * Nota: A area da cerca e definida no mapa (EditItemView)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Place as PlaceIcon,
  Settings as SettingsIcon,
  Tune as TuneIcon,
  CheckCircle as ReviewIcon,
} from '@mui/icons-material';

import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { geofencesActions } from '../../store';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import SelectField from '../../common/components/SelectField';
import EditAttributesAccordion from './EditAttributesAccordion';
import useGeofenceAttributes from '../../common/attributes/useGeofenceAttributes';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            error={!!errors?.name}
            helperText={errors?.name}
            autoFocus
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={t('sharedDescription')}
            value={data.description || ''}
            onChange={(e) => updateData({ description: e.target.value })}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// STEP 2: CONFIGURACOES
// ============================================================================

const ConfigStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const handleHideChange = (e) => {
    updateData({
      attributes: {
        ...data.attributes,
        hide: e.target.checked,
      },
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SelectField
            fullWidth
            value={data.calendarId || ''}
            onChange={(e) => updateData({ calendarId: e.target.value ? Number(e.target.value) : null })}
            endpoint="/api/calendars"
            label={t('sharedCalendar')}
            className="premium-input"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: colors.surfaceHover,
              borderRadius: tokens?.radius?.md || '8px',
              border: `1px solid ${colors.borderSubtle}`,
              px: 2,
              py: 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.attributes?.hide || false}
                  onChange={handleHideChange}
                  size="small"
                  sx={{
                    color: colors.textSecondary,
                    '&.Mui-checked': { color: colors.accent },
                  }}
                />
              }
              label={
                <Typography sx={{ color: colors.textPrimary, fontSize: '14px' }}>
                  {t('sharedFilterMap')}
                </Typography>
              }
            />
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          p: 2,
          borderRadius: tokens?.radius?.md || '8px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('geofenceCalendarHelp') || 'O calendario define quando a cerca virtual esta ativa. Se nenhum calendario for selecionado, a cerca estara sempre ativa.'}
        </Typography>
      </Box>
    </Box>
  );
};

// ============================================================================
// STEP 3: ATRIBUTOS
// ============================================================================

const AttributesStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const geofenceAttributes = useGeofenceAttributes(t);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: '10px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('wizardAttributesHelp')}
        </Typography>
      </Box>

      <EditAttributesAccordion
        attributes={data.attributes}
        setAttributes={(attributes) => updateData({ attributes })}
        definitions={geofenceAttributes}
      />
    </Box>
  );
};

// ============================================================================
// STEP 4: REVISAO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const attributeCount = data.attributes ? Object.keys(data.attributes).filter((k) => k !== 'hide').length : 0;
  const hasArea = !!data.area;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      {/* Aviso se nao tem area definida */}
      {!hasArea && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            backgroundColor: colors.warningBg || 'rgba(210, 153, 34, 0.15)',
            borderRadius: tokens?.radius?.md || '8px',
            border: `1px solid ${colors.warning || '#d29922'}40`,
          }}
        >
          <PlaceIcon sx={{ color: colors.warning || '#d29922' }} />
          <Typography sx={{ color: colors.textPrimary, fontSize: '13px' }}>
            {t('geofenceAreaRequired') || 'A area da cerca sera definida no mapa apos salvar.'}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedName')} value={data.name} />
        <InfoRow label={t('sharedDescription')} value={data.description || '-'} />
        <InfoRow label={t('sharedCalendar')} value={data.calendarId ? `ID: ${data.calendarId}` : '-'} />
        <InfoRow label={t('sharedFilterMap')} value={data.attributes?.hide ? t('sharedYes') : t('sharedNo')} />
        <InfoRow label={t('sharedAttributes')} value={attributeCount > 0 ? `${attributeCount} ${t('sharedAttributes').toLowerCase()}` : '-'} />
        {hasArea && <InfoRow label={t('sharedArea')} value={t('sharedDefined') || 'Definida'} />}
      </Box>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const GeofenceFormWizard = ({ geofence, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardGeofenceBasicDescription') || 'Identificacao da cerca',
      icon: <PlaceIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.name?.trim()) errors.name = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.name },
      ],
    },
    {
      label: t('sharedExtra') || 'Config',
      title: t('sharedExtra') || 'Configuracoes',
      description: t('wizardGeofenceConfigDescription') || 'Calendario e visibilidade',
      icon: <SettingsIcon sx={{ fontSize: 18 }} />,
      component: ConfigStep,
      optional: true,
    },
    {
      label: t('sharedAttributes'),
      title: t('sharedAttributes'),
      description: t('wizardAttributesDescription'),
      icon: <TuneIcon sx={{ fontSize: 18 }} />,
      component: AttributesStep,
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = geofence?.id ? `/api/geofences/${geofence.id}` : '/api/geofences';
      const method = geofence?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const savedGeofence = await response.json();
        dispatch(geofencesActions.update([savedGeofence]));
        onSave?.(savedGeofence);
        navigate('/geofences');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/geofences');
  };

  const initialData = geofence || {
    name: '',
    description: '',
    calendarId: null,
    area: '',
    attributes: {},
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={geofence?.id ? null : 'geofence-wizard-draft'}
      completeButtonText={geofence?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      skipButtonText={t('wizardSkip')}
      showSummary
    />
  );
};

export default GeofenceFormWizard;
